import { CashLocationMutationType } from "@repo/database";
import { MutationSchema } from "@repo/validators";
import { useTranslation } from "react-i18next";

import { useCashLocation } from "~/hooks/use-cash-location";
import { useGeneral } from "~/hooks/use-general";
import { useCashLocationManager } from "~/managers/use-cash-location-manager";
import { usePermissionStore } from "~/stores/use-permission-store";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { ModalInput } from "../ui/modal-input";
import { MultiSelect } from "../ui/multi-select";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Textarea } from "../ui/textarea";

export default function MutationForm({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation("common");
  const { currentEmployee } = usePermissionStore();
  const { vatRates } = useGeneral();
  const { cashLocations, cashLocationMutationCategories } = useCashLocation();
  const { createMutation } = useCashLocationManager();

  const vatRateOptions = Object.values(vatRates).map((rate) => ({
    value: rate.value.toString(),
    label: rate.name,
  }));

  const cashLocationOptions = cashLocations.map((location) => ({
    value: location.id,
    label: location.name,
  }));

  const categoryOptions = Object.values(cashLocationMutationCategories).map(
    (category) => ({
      value: category.id,
      label: category.name,
    }),
  );

  console.log("test");

  const form = useForm({
    schema: MutationSchema(i18n),
    defaultValues: {
      cashLocationId: undefined,
      employeeId: currentEmployee?.id,
      type: CashLocationMutationType.out,
      category: undefined,
      value: 0,
      vatRate: 0,
      description: "",
    },
  });

  const handleSubmit = form.handleSubmit(async (formData) => {
    const { error } = await createMutation(formData);
    if (!error) {
      form.reset();
      onClose();
    }
  });

  return (
    <Form {...form}>
      <form
        id="mutation-form"
        onSubmit={handleSubmit}
        className="grid grid-cols-2 gap-4"
      >
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem className="col-span-full gap-2">
              <FormLabel>{t("Mutation.type")}</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex flex-col space-y-1"
                >
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="in" />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {t("Mutation.Type.in")}
                    </FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-3 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="out" />
                    </FormControl>
                    <FormLabel className="font-normal">
                      {t("Mutation.Type.out")}
                    </FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="value"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Mutation.value")}</FormLabel>
              <FormControl>
                <ModalInput type="decimal" {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="vatRate"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Mutation.vat_rate")}</FormLabel>
              <FormControl>
                <MultiSelect
                  maxSelect={1}
                  placeholder={t("Mutation.vat_rate")}
                  onValueChange={(value) => {
                    field.onChange(parseInt(value[0]));
                  }}
                  options={vatRateOptions}
                  defaultValue={field.value ? [field.value.toString()] : []}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="cashLocationId"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Mutation.cash_location")}</FormLabel>
              <FormControl>
                <MultiSelect
                  maxSelect={1}
                  placeholder={t("Mutation.cash_location")}
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                  options={cashLocationOptions}
                  defaultValue={field.value ? [field.value.toString()] : []}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Mutation.category")}</FormLabel>
              <FormControl>
                <MultiSelect
                  maxSelect={1}
                  placeholder={t("Mutation.category")}
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                  options={categoryOptions}
                  defaultValue={field.value ? [field.value.toString()] : []}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Mutation.description")}</FormLabel>
              <FormControl>
                <Textarea {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
